/**
 * Surface identity — which pane/tab a component is rendering inside.
 *
 * Module-level stores are singletons, so any state they hold that belongs to
 * *one* surface is shared by every copy of that surface. The split pane already
 * mounts two route instances, and under the live-tabs design
 * (`docs/plans/2026-09-11-live-tabs-design.md`) every open tab stays mounted —
 * so "which document is open", "which conversation am I showing" and similar
 * must be keyed, not global.
 *
 * This is the key. A surface id is the workspace tab id, stable for the life of
 * the tab, and is provided by both panes — the primary from
 * `primaryActiveTabId`, the secondary from its own tab.
 *
 * Content caches (a documents-by-id map) and user preferences (a panel width)
 * are deliberately NOT keyed: they are global by nature and sharing them is the
 * point.
 */
import { createContext, useContext } from 'react';

/**
 * Identity for code rendering outside any workspace pane — root-level modals,
 * tests, anything mounted above the split. Deliberately not a real tab id, so
 * such code gets its own bucket rather than silently joining the primary pane's.
 */
export const DEFAULT_SURFACE_ID = 'app';

export const SurfaceContext = createContext<string | null>(null);

/**
 * The id of the surface this component is rendering in.
 *
 * Never throws and never returns null: a consumer rendered outside a pane gets
 * {@link DEFAULT_SURFACE_ID}, so adding surface-keying to a store does not force
 * every caller to handle an absent provider.
 */
export function useSurfaceId(): string {
  return useContext(SurfaceContext) ?? DEFAULT_SURFACE_ID;
}
